import StandardPhotoGrid from '@/components/layout/StandardPhotoGrid';
import { TPhoto } from '@/interfaces/photos';
import { packs, social_links, clips } from '@prisma/client';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EditMoodBoard from '../../components/moodboard/EditMoodBoard';
import Button from '../../components/shared/Button';
import ColorSwitcher from '../../components/shared/ColorSwticher';
import { DownloadZipButton } from '../../components/shared/DownloadZipButton';
import ModalContainer from '../../components/shared/ModalContainer';
import UpgradeModalFlow from '../../components/shared/UpgradeModalFlow';
import { analytics } from '../../services/analytics';
import { trialActive } from '../../services/user';
import { colorManagerAtom } from '../../store/uiStore';
import { trpc } from '@/utils/trpc';
import AddLinkBlock from '@/components/moodboard/AddLinkBlock';
import SocialLinkModal from '@/components/moodboard/SocialLinkModal';
import HeroImage from '@/components/moodboard/HeroImage';
import MediaCount from '@/components/moodboard/MediaCount';
import MoodBoardDescription from '@/components/moodboard/MoodboardDescription';
import MoodboardTitle from '@/components/moodboard/MoodboardTitle';
import { formatCollectionData, getHeroImages } from '@/services/moodboard';
import ShareButton from '@/components/shared/ShareButton';
import useEvent from '@/hooks/useEvent';
import { EVENTS } from '@/utils/constants';
import OwnershipTipModal from '@/components/moodboard/OwnershipTipModal';
import useBoardDuplication from '@/hooks/useBoardDuplication';

export const LINK_HEIGHT = 400;
export const LINK_GAP = 48;
export const LINK_ROW_SPAN = 4;

const SingleCollectionPage = () => {
  const router = useRouter();
  const collectionUuid = String(router.query.id);
  const [collection, setCollection] = useState<{
    photos: TPhoto[];
    collectionMedia: (TPhoto | packs | social_links | clips)[];
    assetUrls: { urls: string; fileNames: string }[];
    zips: string[];
  } | null>(null);
  const [showEditMoodBoard, setShowEditMoodBoard] = useState(false);
  const screenCaptureRef = useRef<HTMLDivElement>(null);
  const [colorManager] = useAtom(colorManagerAtom);
  const { capture } = useEvent();
  const [upgradeModal, setUpgradeModal] = useState(false);
  const {
    showOwnershipTipModal,
    setShowOwnershipTipModal,
    duplicateBoard,
    duplicationIsLoading,
    isOwnerOfCollection,
  } = useBoardDuplication();
  const [shouldShowSocialLinkModal, setShouldShowSocialLinkModal] = useState(-1); //-1 means not showing, 0 means add new, >0 means edit id
  const defaultMedia = new Array(12).fill(undefined);

  const { data: user } = trpc.users.getAuthenticatedUser.useQuery(undefined, {
    staleTime: Infinity,
  });
  const { data: collectionData, refetch } = trpc.collections.getUserCollectionById.useQuery(
    collectionUuid,
    {
      staleTime: Infinity,
      enabled: !!collectionUuid,
    }
  );

  const publicMoodboardUrl = `${window.location.protocol}//${window.location.host}/public/moodboard/${collectionUuid}`;

  useEffect(() => {
    if (collectionData && user && collectionData.user_id !== user.id) {
      capture({
        event: EVENTS.loadedSharedMoodboard,
        collection_uuid: collectionUuid ?? '',
        otherInfo: {
          uuid: collectionData?.uuid ?? '',
          loaderId: user?.id?.toString() ?? '',
          ownerId: collectionData?.user_id?.toString() ?? '',
        },
      });
    }
  }, [collectionData?.uuid, user?.id]);

  useEffect(() => {
    if (collectionData) {
      capture({
        event: EVENTS.loadedMoodboard,
        collection_uuid: collectionUuid ?? '',
        otherInfo: {
          uuid: collectionUuid ?? '',
        },
      });
    }
  }, [collectionData?.uuid, collectionUuid]);

  useEffect(() => {
    if (collectionData) {
      setCollection(formatCollectionData(collectionData));
    }
  }, [collectionData?.id, collectionData?.bookmarks]);

  const onBoardArchived = () => {
    router.push('/moodboard?filter=boards');
  };

  const handleAddLinkClick = useCallback(() => {
    setShouldShowSocialLinkModal(0);
  }, [setShouldShowSocialLinkModal]);

  const selectedSocialLink = useMemo(() => {
    const bookmark = collectionData?.bookmarks.find(
      (bookmark) => bookmark?.id === shouldShowSocialLinkModal
    );
    return {
      ...bookmark?.link,
      bookmarkId: shouldShowSocialLinkModal,
      note: bookmark?.note,
    } as social_links & {
      bookmarkId: number;
    };
  }, [collectionData, shouldShowSocialLinkModal]);

  const handleSocialLinkModalClose = useCallback(() => {
    setShouldShowSocialLinkModal(-1);
    refetch();
  }, [setShouldShowSocialLinkModal, refetch]);

  const closeSocialModalWithoutRefresh = useCallback(() => {
    setShouldShowSocialLinkModal(-1);
  }, [setShouldShowSocialLinkModal]);

  const heroImages = useMemo(() => {
    return getHeroImages(collectionData);
  }, [collectionData]);

  const color = useMemo(() => {
    if (colorManager.text === 'text-black') {
      return 'black';
    } else if (colorManager.text === 'text-white') {
      return 'white';
    } else {
      return 'white';
    }
  }, [colorManager]);

  const handleEditMoodBoardClick = useCallback(() => {
    if (isOwnerOfCollection) {
      setShowEditMoodBoard(!showEditMoodBoard);
    } else {
      setShowOwnershipTipModal(true);
    }
  }, [isOwnerOfCollection, showEditMoodBoard, setShowEditMoodBoard]);

  return (
    <>
      <div className="max-w-[2400px] mx-auto">
        {showEditMoodBoard && (
          <div
            className={clsx([
              'fixed top-0 left-0 w-full h-full z-20 opacity-80',
              colorManager.background,
            ])}
            onClick={() => setShowEditMoodBoard(false)}
            style={{ transform: 'translateZ(500px)' }}
          ></div>
        )}

        <div className="my-16 flex justify-between items-center">
          <p
            onClick={() => router.back()}
            className="font-nhg font-normal uppercase leading-[1] tracking-[0.15em] relative inline-block text-sm 3xl:text-[15px] 4xl:text-[16px] after:hover:content-[''] after:hover:absolute after:hover:-bottom-[3px] after:hover:left-0 after:hover:w-full after:hover:h-px after:hover:bg-white cursor-pointer"
          >
            {collectionData && isOwnerOfCollection ? 'Back' : ''}
          </p>

          <div className="relative flex flex-col items-end gap-4">
            {collectionData && (
              <p
                className={clsx([
                  'font-nhg font-normal uppercase leading-[1] tracking-[0.15em] relative inline-block text-sm 3xl:text-[15px] 4xl:text-[16px] cursor-pointer',
                  showEditMoodBoard ? 'z-20' : '',
                ])}
                onClick={handleEditMoodBoardClick}
              >
                Edit moodboard
              </p>
            )}

            {collectionData && (
              <ColorSwitcher context={{ collectionId: Number(collectionData.id) }} />
            )}

            {showEditMoodBoard && (
              <div
                className="absolute border right-0 top-[30px] z-20"
                style={{
                  transform: 'translateZ(500px)',
                  borderColor: 'currentColor',
                }}
              >
                {collectionData && (
                  <EditMoodBoard
                    collection={collectionData}
                    onSaved={() => {
                      refetch();
                      setShowEditMoodBoard(false);
                    }}
                    onDelete={onBoardArchived}
                    onClose={() => setShowEditMoodBoard(false)}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {collectionData && (
          <>
            <div className="flex flex-col">
              <MoodboardTitle title={collectionData.name} />
              <HeroImage heroImages={heroImages} />
              <MediaCount collectionData={collectionData} />
              <MoodBoardDescription description={collectionData.description} />

              <div className="flex gap-6 items-center justify-center">
                {collectionData && (
                  <div className="flex gap-2">
                    {trialActive(user) && (
                      <Button onClick={() => setUpgradeModal(true)}>
                        <div className="grid grid-cols-[min-content_min-content] gap-[10px] items-center">
                          <svg
                            width="12"
                            height="16"
                            viewBox="0 0 12 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M6 12.125C6.825 12.125 7.5 11.45 7.5 10.625C7.5 9.8 6.825 9.125 6 9.125C5.175 9.125 4.5 9.8 4.5 10.625C4.5 11.45 5.175 12.125 6 12.125ZM10.5 5.375H9.75V3.875C9.75 1.805 8.07 0.125 6 0.125C3.93 0.125 2.25 1.805 2.25 3.875V5.375H1.5C0.675 5.375 0 6.05 0 6.875V14.375C0 15.2 0.675 15.875 1.5 15.875H10.5C11.325 15.875 12 15.2 12 14.375V6.875C12 6.05 11.325 5.375 10.5 5.375ZM3.675 3.875C3.675 2.5925 4.7175 1.55 6 1.55C7.2825 1.55 8.325 2.5925 8.325 3.875V5.375H3.675V3.875ZM10.5 14.375H1.5V6.875H10.5V14.375Z"
                              fill="white"
                            />
                          </svg>
                          Download All
                        </div>
                      </Button>
                    )}

                    {!trialActive(user) && (
                      <DownloadZipButton
                        assetUrls={collection?.assetUrls.map((a) => a.urls) ?? []}
                        zips={collection?.zips ?? []}
                        text="DOWNLOAD ALL"
                        fileName={`DTS Moodboard - ${collectionData.name}`}
                        fileNames={collection?.assetUrls.map((a) => a.fileNames) ?? []}
                        onClick={() => {
                          analytics.track('Downloaded Moodboard Zip', {
                            collectionId: Number(collectionData.id),
                            photoCount: collection?.assetUrls?.length,
                            videoCount: collection?.zips?.length,
                          });
                        }}
                      />
                    )}

                    <ShareButton
                      borderColor={color}
                      textColor={colorManager.text}
                      url={publicMoodboardUrl}
                    />

                    <Button
                      type="secondary"
                      onClick={duplicateBoard}
                      loading={duplicationIsLoading}
                      className={`${colorManager.text} border-${color}`}
                    >
                      COPY
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className={clsx('py-32', colorManager.background)} ref={screenCaptureRef}>
              <StandardPhotoGrid
                media={
                  collection?.collectionMedia?.length ? collection?.collectionMedia : defaultMedia
                }
                gridItemWidth={256}
                gridItemHeight={(LINK_HEIGHT - LINK_GAP * (LINK_ROW_SPAN - 1)) / LINK_ROW_SPAN}
                gridGap={LINK_GAP}
                lazyLoad={false} // have to stop lazy load otherwise PDF export doesn't have the loaded images
                firstItemComponent={
                  isOwnerOfCollection ? <AddLinkBlock onClick={handleAddLinkClick} /> : undefined
                }
                onEditLink={setShouldShowSocialLinkModal}
                defaultGridRows={LINK_ROW_SPAN}
                displayNote={true}
                showRankingScore={true}
                showTrendButton={true}
              />
            </div>
          </>
        )}
      </div>
      {upgradeModal && (
        <ModalContainer onClose={() => setUpgradeModal(false)}>
          <UpgradeModalFlow onClose={() => setUpgradeModal(false)} />
        </ModalContainer>
      )}

      {shouldShowSocialLinkModal > -1 && collectionData && (
        <ModalContainer className="border-0" onClose={closeSocialModalWithoutRefresh}>
          <SocialLinkModal
            collection={collectionData}
            selectedSocialLink={selectedSocialLink}
            closeSelf={handleSocialLinkModalClose}
            refetchCollectionData={refetch}
          />
        </ModalContainer>
      )}

      {showOwnershipTipModal && (
        <ModalContainer onClose={() => setShowOwnershipTipModal(false)}>
          <OwnershipTipModal
            onClose={() => setShowOwnershipTipModal(false)}
            onCopy={duplicateBoard}
            isLoading={duplicationIsLoading}
          />
        </ModalContainer>
      )}
    </>
  );
};

export default SingleCollectionPage;
